

import React, { useState, useEffect } from 'react';
import { auth, database } from '../firebase';
import { ref, push, update, serverTimestamp } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';

const CustomerForms = ({ onClose, onSuccess, customer }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    phoneNo: '',
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (customer) {
      setFormData({
        customerName: customer.customerName || '',
        phoneNo: customer.phoneNo || '',
      });
    }
  }, [customer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNo') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: numericValue,
      });
    } else if (name === 'customerName') {
      const alphaValue = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData({
        ...formData,
        [name]: alphaValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage('');
  setMessageType('');
  if (!currentUser) {
    setMessage('You must be logged in to add or edit a customer.');
    setMessageType('error');
    return;
  }
  if (!formData.customerName) {
    setMessage('Customer Name is required.');
    setMessageType('error');
    return;
  }
  if (!/^[a-zA-Z\s]+$/.test(formData.customerName)) {
    setMessage('Customer Name must contain only alphabets.');
    setMessageType('error');
    return;
  }
  if (!formData.phoneNo || formData.phoneNo.length !== 10) {
    setMessage('Phone No must be exactly 10 digits.');
    setMessageType('error');
    return;
  }
  try {
    const encodedEmail = encodeEmailForPath(currentUser.email);
    const customersRef = ref(database, `users/${encodedEmail}/customers`);
    const customerData = {
      ...formData,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.email,
    };
    if (customer && customer.id) {
      const customerRef = ref(database, `users/${encodedEmail}/customers/${customer.id}`);
      await update(customerRef, customerData);
      alert('Customer updated successfully!');
      setMessage('Customer updated successfully!');
    } else {
      customerData.createdAt = serverTimestamp();
      customerData.createdBy = currentUser.email;
      await push(customersRef, customerData);
      alert('Customer added successfully!');
      setMessage('Customer added successfully!');
    }
    setMessageType('success');
    if (onSuccess) {
      onSuccess({
        customerName: formData.customerName,
        phoneNo: formData.phoneNo,
      });
    }
    setFormData({
      customerName: '',
      phoneNo: '',
    });
    setTimeout(() => {
      onClose();
    }, 1000);
  } catch (error) {
    console.error('Error saving customer:', error);
    alert(`Error: ${error.message}`);
    setMessage(`Error: ${error.message}`);
    setMessageType('error');
  }
};

  const styles = {
    modal: {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    },
    form: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '80vh',
      overflowY: 'auto',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    },
    header: {
      backgroundColor: '#2563eb',
      color: 'white',
      padding: '12px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '16px',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      margin: '-20px -20px 16px -20px',
    },
    row: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px',
      marginBottom: '12px',
    },
    group: {
      display: 'flex',
      flexDirection: 'column',
    },
    label: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '4px',
    },
    input: {
      padding: '8px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      outline: 'none',
      fontSize: '12px',
      width: '90%',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s, box-shadow 0.2s', // Added transition for smooth highlighting
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px',
      marginTop: '16px',
    },
    saveButton: {
      padding: '8px 16px',
      backgroundColor: '#10b981',
      color: 'white',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '12px',
      transition: 'all 0.2s ease',
    },
    cancelButton: {
      padding: '8px 16px',
      backgroundColor: '#ef4444',
      color: 'white',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '12px',
      transition: 'all 0.2s ease',
    },
    message: {
      padding: '8px',
      marginBottom: '16px',
      borderRadius: '4px',
      color: 'white',
      fontSize: '12px',
    },
    sectionHeading: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#1f2937',
    },
  };

  return (
    <div style={styles.modal}>
      <div style={styles.form}>
        <div style={styles.header}>
          <span>{customer ? 'EDIT CUSTOMER' : 'ADD CUSTOMER'}</span>
          <button
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {message && (
          <div
            style={{
              ...styles.message,
              backgroundColor: messageType === 'error' ? '#fee2e2' : '#d1fae5',
              color: messageType === 'error' ? '#b91c1c' : '#065f46',
            }}
          >
            {message}
          </div>
        )}
        {loading && <div style={styles.message}>Loading...</div>}
        <form onSubmit={handleSubmit}>
          <div style={styles.row}>
            <div style={styles.group}>
              <label style={styles.label}>CUSTOMER NAME *</label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter customer name"
                required
                pattern="[a-zA-Z\s]+"
                title="Only alphabets and spaces allowed"
                autoComplete="off"
                onFocus={(e) => (e.target.style.borderColor = '#3b82f6')} // Highlight on focus
                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')} // Reset on blur
              />
            </div>
            <div style={styles.group}>
              <label style={styles.label}>PHONE NO *</label>
              <input
                type="text"
                name="phoneNo"
                value={formData.phoneNo}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter 10 digit phone number"
                maxLength={10}
                pattern="\d{10}"
                required
                inputMode="numeric"
                autoComplete="off"
                onFocus={(e) => (e.target.style.borderColor = '#3b82f6')} // Highlight on focus
                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')} // Reset on blur
              />
            </div>
          </div>
          <div style={styles.buttonContainer}>
            <button
              type="submit"
              style={styles.saveButton}
              onMouseEnter={(e) => (e.target.style.filter = 'brightness(90%)')}
              onMouseLeave={(e) => (e.target.style.filter = 'brightness(100%)')}
            >
              {customer ? 'Update Customer' : 'Add Customer'}
            </button>
            <button
              type="button"
              style={styles.cancelButton}
              onClick={onClose}
              onMouseEnter={(e) => (e.target.style.filter = 'brightness(90%)')}
              onMouseLeave={(e) => (e.target.style.filter = 'brightness(100%)')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerForms;